// Shared helpers for the components/ui adapters.
//
// Every file in this folder is a thin ADAPTER: the shadcn-shaped names and
// composition the pages already use, rendered by the hx implementation
// (components/hx + the `hx-*` classes in shell-helix-data.css). Nothing here
// carries a colour — the hx classes read theme.css tokens, and the adapters
// only translate props and class hints.

import * as React from "react";
import { helixRoot } from "../hx/hooks";

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

/** Drop Tailwind padding utilities (`p-4`, `pt-0`, `px-3`, …) from a class
 *  list. The hx card owns its own padding; the shadcn pages padded each
 *  section because the shadcn card had none. */
export function stripPadding(className?: string): string | undefined {
  if (!className) return className;
  const kept = className.split(/\s+/).filter((t) => t && !/^-?p[trblxyse]?-/.test(t));
  return kept.length ? kept.join(" ") : undefined;
}

/** True when the class list already sizes the control's width (`w-32`,
 *  `w-full`, `basis-*`), so the adapter should not impose `width: 100%`. */
export function hasWidthUtility(className?: string): boolean {
  return !!className && /(^|\s)(w-|basis-)/.test(className);
}

/** The shadcn Input / Textarea filled their container (`w-full`). Keep that
 *  default unless the page sized the control itself. */
export function fillWidth(className: string | undefined, style: React.CSSProperties | undefined): React.CSSProperties | undefined {
  if (hasWidthUtility(className)) return style;
  return { width: "100%", ...style };
}

/** Plain text of a React tree — the label an `<SelectItem>` shows. */
export function textOf(node: React.ReactNode): string {
  return rawText(node).replace(/\s+/g, " ").trim();
}
function rawText(node: React.ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(rawText).filter(Boolean).join(" ");
  if (React.isValidElement(node)) return rawText((node.props as { children?: React.ReactNode }).children);
  return "";
}

/** Tailwind `max-w-*` → hx modal size. */
export function modalSizeFrom(className?: string, style?: React.CSSProperties): "md" | "lg" | "xl" {
  const w = typeof style?.maxWidth === "number" ? style.maxWidth : undefined;
  if (w != null) return w >= 900 ? "xl" : w >= 600 ? "lg" : "md";
  const m = className?.match(/(?:^|\s)(?:sm:|md:|lg:)?max-w-(\w+)/);
  const t = m?.[1];
  if (!t) return "md";
  if (/^(3xl|4xl|5xl|6xl|7xl|screen|full|none)$/.test(t)) return "xl";
  if (/^(xl|2xl)$/.test(t)) return "lg";
  return "md";
}

/** Tailwind `max-w-*` → drawer width in px. */
export function drawerWidthFrom(className?: string, style?: React.CSSProperties): number | undefined {
  if (typeof style?.width === "number") return style.width;
  if (typeof style?.maxWidth === "number") return style.maxWidth;
  const m = className?.match(/(?:^|\s)(?:sm:|md:|lg:)?max-w-(\w+)/);
  const map: Record<string, number> = { xs: 320, sm: 384, md: 448, lg: 512, xl: 576, "2xl": 672, "3xl": 768, "4xl": 896, "5xl": 1024 };
  return m ? map[m[1]] : undefined;
}

/** Portal target for the Radix-backed adapters (popover, menu, tooltip).
 *  The `.helix` root is where the tokens and `hx-*` rules live, so a floating
 *  panel portalled to `document.body` would lose both. Resolved after mount —
 *  on the first render the shell's root is not in the DOM yet. */
export function useHelixContainer(): Element | undefined {
  const [el, setEl] = React.useState<Element | undefined>(undefined);
  React.useEffect(() => { setEl(helixRoot()); }, []);
  return el;
}

/** Split a Radix-style `Content` child list into the named slots. */
export function splitSlots(
  children: React.ReactNode,
  types: { header: React.ElementType; title: React.ElementType; description: React.ElementType; footer: React.ElementType },
): { title?: React.ReactElement; description?: React.ReactElement; footer?: React.ReactElement; body: React.ReactNode[] } {
  let title: React.ReactElement | undefined;
  let description: React.ReactElement | undefined;
  let footer: React.ReactElement | undefined;
  const body: React.ReactNode[] = [];
  for (const child of React.Children.toArray(children)) {
    if (!React.isValidElement(child)) { body.push(child); continue; }
    if (child.type === types.header) {
      for (const c of React.Children.toArray((child.props as { children?: React.ReactNode }).children)) {
        if (React.isValidElement(c) && c.type === types.title) title = c;
        else if (React.isValidElement(c) && c.type === types.description) description = c;
        else body.push(c);
      }
      continue;
    }
    if (child.type === types.title) { title = child; continue; }
    if (child.type === types.description) { description = child; continue; }
    if (child.type === types.footer) { footer = child; continue; }
    body.push(child);
  }
  return { title, description, footer, body };
}

export const noop = (): void => {};
